import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../components/Modal';
import Dropdown from '../../../../components/Dropdown';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import useFetch from '../../../../hooks/useFetch';
import CommonService from '../../../../services/common.service';
import PropertyService from '../../../../services/property.service';
import { APP_SUCCESS_MESSAGES } from '../../../../utils/appConstant';

interface SyncPropertiesModalProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_STATE_CODE = 'GJ';

const SyncPropertiesModal: React.FC<SyncPropertiesModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [selectedStateCode, setSelectedStateCode] = useState<string>('');
  const [stateSearch, setStateSearch] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '', countryId: '1' },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: stateSearch, countryId: '1' },
    dependency: [stateSearch],
  });

  const stateOptions = useMemo(
    () => (states || []).map((s: any) => ({ label: s.name, value: s.code })),
    [states]
  );

  const allStateOptions = useMemo(
    () => (allStates || []).map((s: any) => ({ label: s.name, value: s.code })),
    [allStates]
  );

  useEffect(() => {
    if (allStates?.length && !selectedStateCode) {
      const gujarat = (allStates as any[]).find((s: any) => s.code === DEFAULT_STATE_CODE);
      if (gujarat) setSelectedStateCode(DEFAULT_STATE_CODE);
    }
  }, [allStates, selectedStateCode]);

  const handleStateChange = (e: any) => {
    setSelectedStateCode(e.target.value);
    setSubmitError('');
  };

  const handleStateSearch = (e: any) => {
    setStateSearch(e.target.value);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await PropertyService.syncGovernmentProjects({ state_code: selectedStateCode });
      setSelectedStateCode('');
      setStateSearch('');
      onClose();
      toast.success(
        <Toast message={APP_SUCCESS_MESSAGES.propertySyncQueued} />,
        { icon: false }
      );
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message || t('common.errorMessages.internalServerError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setSelectedStateCode('');
    setStateSearch('');
    setSubmitError('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      disableBackDrop={submitting}
      title={t('properties.list.pageTexts.syncModal.title')}
      content={
        <div className="py-2">
          <Dropdown
            id="sync-state"
            label={t('properties.list.pageTexts.syncModal.stateLabel')}
            name="state"
            required
            values={selectedStateCode ? [selectedStateCode] : []}
            options={stateOptions}
            allOptions={allStateOptions || []}
            searchText={stateSearch}
            onChange={handleStateChange}
            onSearch={handleStateSearch}
          />
          {submitError && (
            <p className="text-danger mt-3 mb-0 small">{submitError}</p>
          )}
        </div>
      }
      action={
        <>
          <Button
            className="btn btn-outline-secondary"
            onClick={handleClose}
            text={t('common.buttons.cancel')}
            disabled={submitting}
          />
          <Button
            className="btn btn-primary ms-2"
            onClick={handleSubmit}
            text={t('properties.list.pageTexts.syncModal.startSync')}
            loading={submitting}
            disabled={!selectedStateCode}
          />
        </>
      }
    />
  );
};

export default SyncPropertiesModal;
